import React, { useState, useEffect } from 'react';
import { Module } from '../../types/auth';
import './ModuleModal.css';

interface ModuleModalProps {
  module: Module | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (moduleData: Module) => void;
}

const ModuleModal: React.FC<ModuleModalProps> = ({
  module,
  isOpen,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<Partial<Module>>({
    title: '',
    subtitle: '',
    description: '',
    image_url: '',
    video_url: '',
    duration: 0,
    no_of_contents: 0,
    module_order: 1,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form data when modal opens for a new module
  const resetFormData = () => {
    setFormData({
      title: '',
      subtitle: '',
      description: '',
      image_url: '',
      video_url: '',
      duration: 0,
      no_of_contents: 0,
      module_order: 1,
    });
    setErrors({});
  };

  useEffect(() => {
    if (module) {
      setFormData({
        title: module.title || '',
        subtitle: module.subtitle || '',
        description: module.description || '',
        image_url: module.image_url || '',
        video_url: module.video_url || '',
        duration: module.duration || 0,
        no_of_contents: module.no_of_contents || 0,
        module_order: module.module_order || 1,
      });
    } else {
      // Reset form for new module creation
      resetFormData();
    }
    setErrors({});
  }, [module]);

  // Reset form when modal is opened for new module creation
  useEffect(() => {
    if (isOpen && !module) {
      resetFormData();
    }
  }, [isOpen, module]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title?.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.image_url?.trim()) {
      newErrors.image_url = 'Image URL is required';
    }

    if (!formData.duration || formData.duration <= 0) {
      newErrors.duration = 'Duration must be greater than 0';
    }

    if (!formData.no_of_contents || formData.no_of_contents <= 0) {
      newErrors.no_of_contents = 'Number of contents must be greater than 0';
    }

    if (!formData.module_order || formData.module_order <= 0) {
      newErrors.module_order = 'Module order must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const moduleData: Module = {
      id: module?.id || '',
      title: formData.title || '',
      subtitle: formData.subtitle || '',
      description: formData.description || '',
      image_url: formData.image_url || '',
      video_url: formData.video_url || '',
      duration: formData.duration || 0,
      no_of_contents: formData.no_of_contents || 0,
      creator_id: module?.creator_id || '',
      quest_id: module?.quest_id || '',
      module_order: formData.module_order || 1,
    };

    onSave(moduleData);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]:
        name === 'duration' ||
        name === 'no_of_contents' ||
        name === 'module_order'
          ? parseInt(value) || 0
          : value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {module ? 'Edit Module' : 'Create Module'}
          </h2>
          <button className="modal-close" onClick={onClose}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <form className="modal-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">
              Title <span className="required">*</span>
            </label>
            <input
              type="text"
              name="title"
              className={`form-input ${errors.title ? 'error' : ''}`}
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Enter module title"
            />
            {errors.title && (
              <span className="error-message">{errors.title}</span>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Subtitle</label>
            <input
              type="text"
              name="subtitle"
              className="form-input"
              value={formData.subtitle}
              onChange={handleInputChange}
              placeholder="Enter module subtitle"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              name="description"
              className="form-textarea"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter module description"
              rows={3}
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              Image URL <span className="required">*</span>
            </label>
            <input
              type="url"
              name="image_url"
              className={`form-input ${errors.image_url ? 'error' : ''}`}
              value={formData.image_url}
              onChange={handleInputChange}
              placeholder="Enter image URL"
            />
            {errors.image_url && (
              <span className="error-message">{errors.image_url}</span>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Video URL</label>
            <input
              type="url"
              name="video_url"
              className="form-input"
              value={formData.video_url}
              onChange={handleInputChange}
              placeholder="Enter video URL"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                Duration (minutes) <span className="required">*</span>
              </label>
              <input
                type="number"
                name="duration"
                className={`form-input ${errors.duration ? 'error' : ''}`}
                value={formData.duration}
                onChange={handleInputChange}
                placeholder="Enter duration"
                min="1"
              />
              {errors.duration && (
                <span className="error-message">{errors.duration}</span>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                Number of Contents <span className="required">*</span>
              </label>
              <input
                type="number"
                name="no_of_contents"
                className={`form-input ${errors.no_of_contents ? 'error' : ''}`}
                value={formData.no_of_contents}
                onChange={handleInputChange}
                placeholder="Enter number of contents"
                min="1"
              />
              {errors.no_of_contents && (
                <span className="error-message">{errors.no_of_contents}</span>
              )}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              Module Order <span className="required">*</span>
            </label>
            <input
              type="number"
              name="module_order"
              className={`form-input ${errors.module_order ? 'error' : ''}`}
              value={formData.module_order}
              onChange={handleInputChange}
              placeholder="Enter module order"
              min="1"
            />
            {errors.module_order && (
              <span className="error-message">{errors.module_order}</span>
            )}
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {module ? 'Update Module' : 'Create Module'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModuleModal;
